import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PauseIcon from "@mui/icons-material/Pause";
import PlayCircleFilled from "@mui/icons-material/PlayCircleFilled";
import ReplayIcon from "@mui/icons-material/Replay";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Box, CircularProgress, IconButton, Tooltip } from "@mui/material";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import { Report } from "src/types/Report";
import { scheduleStatus } from "src/types/ScheduleJobStatus";
import {
  deleteJob,
  getScheduledJobs,
  pause,
  resume,
} from "../../clients/ScheduleJobService";
import { useInterval } from "../../hooks/useInterval";
import { login, selectUser } from "../../redux/slices/userSlice";
import { ScheduleJobTypes } from "../../types/ScheduleJobTypes";
import { convertUTCtoLocal, formatDateStr } from "../../utils/DateUtil";
import WarningPopup from "../modal/WarningPopup";
import FilterComponent from "../Reports/FilterComponent";
import reportScreenStylings from "../Reports/ReportsScreen.module.css";
import Pagination from "../templates/Pagination";

const ScheduleScreen: React.FC = () => {
  const user = useSelector(selectUser);
  const reportFilterData: any = {
    productCode: user.productCode,
    clientCode: user.selectedClient ?? user.clientCode,
  };
  const initialData = () => {
    let initialData = { ...reportFilterData };
    if (user.role === "USER") {
      initialData.reportUser = user.userId;
    }
    return initialData;
  };

  const [scheduleJobRecords, setScheduleJobRecords] = useState<
    ScheduleJobTypes[] | undefined
  >(undefined);
  const [scheduleJobRecord, setScheduleJobRecord] = useState<
    ScheduleJobTypes | undefined
  >(undefined);
  const [filterScheduleJobRecords, setFilterScheduleJobRecords] =
    useState<any>(initialData);
  const [loading, setLoading] = useState<boolean>(false);
  const [deletePopup, setDeletePopup] = useState<boolean>(false);
  const [rows, setRows] = useState("5");
  const [scheduleUserList, setScheduleUserList] = useState<
    Set<string> | undefined
  >(undefined);
  const [currentPageScheduleScreen, setCurrentPageScheduleScreen] = useState(1);
  const reportsPerPage = Number(rows);
  const indexOfLastScheduleRecord = currentPageScheduleScreen * reportsPerPage;
  const indexOfFirstScheduleRecord = indexOfLastScheduleRecord - reportsPerPage;
  const currentScheduleRecords = scheduleJobRecords?.slice(
    indexOfFirstScheduleRecord,
    indexOfLastScheduleRecord
  );
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const filterdata = async (scheduleFormData: any) => {
    try {
      const response = await getScheduledJobs(user.auth, scheduleFormData);
      const data = await response.json();
      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }
      const filterData = data.reports.filter(
        (job: ScheduleJobTypes) => job.deletedFlag === false
      );
      if (user.role !== "USER") {
        const reportUserSet = new Set<string>(
          filterData.map((report: Report) => report.reportUser)
        );
        setScheduleUserList(() => new Set([...Array.from(reportUserSet)]));
      }
      setScheduleJobRecords(filterData);
      if (filterData.length > 0) setCurrentPageScheduleScreen(1);
    } catch (error) {
      if (user.role !== "USER") setScheduleUserList(new Set());
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
    dispatch(
      login({
        userDetailsId: user.userDetailsId,
        userId: user.userId,
        password: user.password,
        email: user.email,
        role: user.role,
        permissionsList: user.permissionsList,
        productCode: user.productCode,
        firstName: user.firstName,
        lastName: user.lastName,
        products: user.products,
        filesStatus: false,
        clientId: user.clientId,
        clientCode: user.clientCode,
        selectedClient: user.selectedClient,
        auth: user.auth,
      })
    );
  };

  const [rendercount, setRenderCount] = useState<number>(0);

  const pauseOrResumeOrDeleteScheduledJob = async (
    ScheduleData: ScheduleJobTypes,
    operation: string
  ) => {
    try {
      setLoading(true);
      setRenderCount((prevCount) => prevCount + 1);
      const formdata = new FormData();
      formdata.append(
        "scheduleJobId",
        (ScheduleData.scheduleJobId ?? 0).toString()
      );
      const response =
        operation === "Pause"
          ? await pause(user.auth, formdata)
          : operation === "Resume"
            ? await resume(user.auth, formdata)
            : await deleteJob(user.auth, formdata);
      const data = await response.json();
      data &&
        filterdata({
          ...filterScheduleJobRecords,
        });
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleItemsPerPageChange = (event: any) => {
    setRows(event.target.value);
    setCurrentPageScheduleScreen(1);
  };
  useEffect(() => {
    filterdata({
      ...filterScheduleJobRecords,
      productCode: user.productCode,
      clientCode: user.selectedClient ?? user.clientCode,
    });
  }, [user.productCode, user.selectedClient]);

  useInterval(
    () => {
      filterdata({
        ...filterScheduleJobRecords,
      });
    },
    parseInt(process.env.REFRESH_INTERVAL ?? "60000", 10)
  );

  useEffect(() => {
    filterdata({
      ...filterScheduleJobRecords,
    });
  }, []);

  useEffect(() => {
    filterdata({
      ...filterScheduleJobRecords,
    });
  }, [rendercount]);

  if (loading || !scheduleJobRecords) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <CircularProgress disableShrink />
      </Box>
    );
  }

  return (
    <>
      {scheduleJobRecords && (
        <Box>
          <Typography
            variant="h5"
            mb={"6px"}
            sx={{ paddingTop: "2.5%" }}
          >
            Schedule Job Details
          </Typography>
          <Box sx={{ margin: "0% 8%" }}>
            <FilterComponent
              Mode="ScheduleScreen"
              usersList={scheduleUserList}
              filterData={filterScheduleJobRecords}
              handleOnBack={(filterReportRecords: Report) => {
                setFilterScheduleJobRecords(filterReportRecords);
                filterdata(filterReportRecords);
              }}
            />

            <Paper sx={{ width: "100%", position: "sticky" }}>
              <TableContainer
                component={Paper}
                sx={{ mt: "2px", maxHeight: ["328px"] }}
              >
                <Table
                  aria-label="Schedule table"
                  stickyHeader
                >
                  <TableHead>
                    <TableRow sx={{ fontWeight: "600" }}>
                      <TableCell
                        align="center"
                        sx={{ fontWeight: "600" }}
                      >
                        Schedule Job Id
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ fontWeight: "600" }}
                      >
                        Frequency
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ fontWeight: "600" }}
                      >
                        Job Time
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ fontWeight: "600" }}
                      >
                        Organization
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ fontWeight: "600" }}
                      >
                        Status
                      </TableCell>

                      <TableCell
                        align="left"
                        sx={{ fontWeight: "600" }}
                      >
                        Job Start Date
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ fontWeight: "600" }}
                      >
                        Job End Date
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ fontWeight: "600" }}
                      >
                        Job Description
                      </TableCell>
                      {user.role !== "USER" && (
                        <TableCell
                          align="left"
                          sx={{ fontWeight: "600" }}
                        >
                          Scheduled By
                        </TableCell>
                      )}
                      <TableCell
                        align="right"
                        sx={{ fontWeight: "600", paddingRight: "18px" }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  {currentScheduleRecords &&
                    currentScheduleRecords.length === 0 && (
                      <TableBody
                        sx={{
                          overflowY: "scroll",
                          overflowX: "scroll",
                        }}
                      >
                        <TableRow>
                          <TableCell
                            align="center"
                            colSpan={10}
                          >
                            <p>No Schedule Jobs</p>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    )}
                  {currentScheduleRecords &&
                    currentScheduleRecords.length > 0 && (
                      <TableBody
                        sx={{ overflowY: "scroll", overflowX: "scroll" }}
                      >
                        {currentScheduleRecords.length > 0 &&
                          currentScheduleRecords.map(
                            (scheduleJobRecord: ScheduleJobTypes) =>
                              !scheduleJobRecord.deletedFlag && (
                                <TableRow
                                  key={scheduleJobRecord.scheduleJobId}
                                  sx={{
                                    "&:last-child td, &:last-child th": {
                                      border: 0,
                                    },
                                  }}
                                >
                                  <TableCell align="center">
                                    <Tooltip
                                      title={scheduleJobRecord.tags}
                                      placement="top"
                                    >
                                      <span>
                                        {
                                          scheduleJobRecord.userSpecificScheduleJobId
                                        }
                                      </span>
                                    </Tooltip>
                                  </TableCell>
                                  <TableCell align="center">
                                    {scheduleJobRecord.jobType}
                                  </TableCell>
                                  <TableCell align="center">
                                    {scheduleJobRecord.jobFrequency}
                                  </TableCell>
                                  <TableCell align="center">
                                    {scheduleJobRecord.countryCode}
                                  </TableCell>
                                  <TableCell align="left">
                                    {scheduleJobRecord.status}
                                  </TableCell>
                                  <TableCell align="left">
                                    {scheduleJobRecord.scheduleJobStartDateTime &&
                                      formatDateStr(
                                        convertUTCtoLocal(
                                          scheduleJobRecord.scheduleJobStartDateTime
                                        ),
                                        "YYYY-MM-DD HH:mm",
                                        true
                                      )}
                                  </TableCell>
                                  <TableCell align="left">
                                    {scheduleJobRecord.scheduleJobEndDate !==
                                    null ? (
                                      formatDateStr(
                                        convertUTCtoLocal(
                                          scheduleJobRecord.scheduleJobEndDate
                                        ),
                                        "YYYY-MM-DD HH:mm",
                                        true
                                      )
                                    ) : (
                                      <p>Loading...</p>
                                    )}
                                  </TableCell>
                                  <TableCell
                                    align="left"
                                    sx={{
                                      maxWidth: "200px",
                                      whiteSpace: "pre-wrap",
                                    }}
                                  >
                                    {scheduleJobRecord.jobDescription}
                                  </TableCell>
                                  {user.role !== "USER" && (
                                    <TableCell
                                      align="left"
                                      sx={{
                                        maxWidth: "200px",
                                        whiteSpace: "pre-wrap",
                                      }}
                                    >
                                      {scheduleJobRecord.reportUser}
                                    </TableCell>
                                  )}
                                  <TableCell align="right">
                                    {scheduleJobRecord.status &&
                                      (scheduleJobRecord.status.includes(
                                        scheduleStatus[5]
                                      ) ||
                                        scheduleJobRecord.status.includes(
                                          scheduleStatus[6]
                                        ) ||
                                        scheduleJobRecord.status.includes(
                                          scheduleStatus[7]
                                        )) && (
                                        <Tooltip
                                          title="Pause Job"
                                          placement="top"
                                        >
                                          <span>
                                            <IconButton
                                              sx={{ padding: "4px" }}
                                              disabled={
                                                scheduleJobRecord.status ===
                                                "Failed"
                                              }
                                              onClick={(e: any) =>
                                                pauseOrResumeOrDeleteScheduledJob(
                                                  scheduleJobRecord,
                                                  "Pause"
                                                )
                                              }
                                            >
                                              <PauseIcon />
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                      )}
                                    {scheduleJobRecord.status &&
                                      scheduleJobRecord.status.includes(
                                        scheduleStatus[4]
                                      ) && (
                                        <Tooltip
                                          title="Resume Job"
                                          placement="top"
                                        >
                                          <span>
                                            <IconButton
                                              sx={{ padding: "4px" }}
                                              onClick={(e: any) =>
                                                pauseOrResumeOrDeleteScheduledJob(
                                                  scheduleJobRecord,
                                                  "Resume"
                                                )
                                              }
                                            >
                                              <PlayCircleFilled />
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                      )}
                                    <Tooltip
                                      title={
                                        scheduleJobRecord.status ===
                                        scheduleStatus[0]
                                          ? "Reschedule Job"
                                          : "Edit Job"
                                      }
                                      placement="top"
                                    >
                                      <span>
                                        <IconButton
                                          sx={{ padding: "4px" }}
                                          onClick={(e: any) => {
                                            reportFilterData.scheduleJobId =
                                              scheduleJobRecord.userSpecificScheduleJobId;
                                            navigate("/configure", {
                                              state: {
                                                editformdata: {
                                                  tags: scheduleJobRecord.tags,
                                                  countrycode:
                                                    scheduleJobRecord.countryCode,
                                                  emails:
                                                    scheduleJobRecord.recipientsEmailIds &&
                                                    scheduleJobRecord.recipientsEmailIds.split(
                                                      ","
                                                    ),
                                                  testDataFiles: null,
                                                  featureFiles: null,
                                                  jobDescription:
                                                    scheduleJobRecord.jobDescription,
                                                  username: user.userId,
                                                  scheduleJobId:
                                                    scheduleJobRecord.status ===
                                                    scheduleStatus[0]
                                                      ? null
                                                      : scheduleJobRecord.userSpecificScheduleJobId,
                                                  jobStartDate:
                                                    scheduleJobRecord.scheduleJobStartDateTime
                                                      ? formatDateStr(
                                                          convertUTCtoLocal(
                                                            scheduleJobRecord.scheduleJobStartDateTime
                                                          ),
                                                          "YYYY-MM-DD HH:mm:ss",
                                                          true
                                                        )
                                                      : null,
                                                  jobExecutionTime:
                                                    scheduleJobRecord.scheduleJobStartDateTime
                                                      ? formatDateStr(
                                                          convertUTCtoLocal(
                                                            scheduleJobRecord.scheduleJobStartDateTime
                                                          ),
                                                          "YYYY-MM-DD HH:mm:ss",
                                                          true
                                                        )
                                                      : null,
                                                  recurrenceDaysOfWeek:
                                                    scheduleJobRecord.recurrenceDaysOfWeek &&
                                                    scheduleJobRecord.recurrenceDaysOfWeek.split(
                                                      ","
                                                    ),
                                                  jobEndDate:
                                                    scheduleJobRecord.scheduleJobEndDate
                                                      ? formatDateStr(
                                                          convertUTCtoLocal(
                                                            scheduleJobRecord.scheduleJobEndDate
                                                          ),
                                                          "YYYY-MM-DD HH:mm:ss",
                                                          true
                                                        )
                                                      : null,
                                                  jobFrequency:
                                                    formatDateStr(
                                                      new Date(),
                                                      "YYYY-MM-DD",
                                                      true
                                                    ) +
                                                    " " +
                                                    scheduleJobRecord.jobFrequency,
                                                  jobType:
                                                    scheduleJobRecord.jobType,
                                                },
                                              },
                                            });
                                          }}
                                        >
                                          {scheduleJobRecord.status ===
                                          scheduleStatus[0] ? (
                                            <ReplayIcon />
                                          ) : (
                                            <EditIcon />
                                          )}
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    <Tooltip
                                      title="View Scheduled Reports"
                                      placement="top"
                                    >
                                      <span>
                                        <IconButton
                                          sx={{ padding: "4px" }}
                                          onClick={(e: any) => {
                                            reportFilterData.scheduleJobId =
                                              scheduleJobRecord.userSpecificScheduleJobId;
                                            if (user.role !== "SYSTEM_ADMIN") {
                                              reportFilterData.clientCode =
                                                user.clientCode;
                                            }
                                            if (user.role === "USER") {
                                              reportFilterData.reportUser =
                                                user.userId;
                                            }
                                            navigate("/reports", {
                                              state: {
                                                filterTableData:
                                                  reportFilterData,
                                              },
                                            });
                                          }}
                                        >
                                          <VisibilityIcon />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    <Tooltip
                                      title="Delete Job"
                                      placement="top"
                                    >
                                      <span>
                                        <IconButton
                                          sx={{ padding: "4px" }}
                                          onClick={(e: any) => {
                                            setScheduleJobRecord(
                                              scheduleJobRecord
                                            );
                                            setDeletePopup(true);
                                          }}
                                        >
                                          <DeleteIcon />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                              )
                          )}
                      </TableBody>
                    )}
                </Table>
              </TableContainer>
            </Paper>
          </Box>
          <Box className={reportScreenStylings.actionBox}>
            <Box sx={{ marginLeft: "7rem" }}>
              <Pagination
                rows={rows}
                currentPage={currentPageScheduleScreen}
                setCurrentPage={setCurrentPageScheduleScreen}
                totalDataLength={scheduleJobRecords.length}
                dataPerPage={reportsPerPage}
                handleItemsPerPageChange={(e) => handleItemsPerPageChange(e)}
                fieldName="Schedule Jobs"
              />
            </Box>
          </Box>
        </Box>
      )}
      {deletePopup && (
        <WarningPopup
          isOpen={true}
          onClose={() => {
            setDeletePopup(false);
          }}
          actionButtons={[
            {
              name: "Confirm",
              onButtonClick: () => {
                if (scheduleJobRecord) {
                  pauseOrResumeOrDeleteScheduledJob(
                    scheduleJobRecord,
                    "Delete"
                  );
                  setDeletePopup(false);
                }
              },
            },
            {
              name: "Cancel",
              onButtonClick: () => setDeletePopup(false),
            },
          ]}
        >
          <Typography>Do you want to delete the scheduled Job</Typography>
        </WarningPopup>
      )}
    </>
  );
};

export default ScheduleScreen;
